import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";

export default function AccidentChart() {
  const [isMobile, setIsMobile] = useState(false);
  const [tooltip, setTooltip] = useState(null);       // {index, year, accidents, fatalities, cx, cy}
  const [tooltipPos, setTooltipPos] = useState(null); // {left, top, placeAbove}

  // refs
  const svgRef = useRef(null);
  const scrollRef = useRef(null);        // wrapper que rola no mobile
  const containerRef = useRef(null);     // container RELATIVE para posicionar tooltip
  const tooltipRef = useRef(null);

  // ---------------- Dados ----------------
  const accidentData = [
    { year: 2010, accidents: 550, fatalities: 33 },
    { year: 2011, accidents: 1028, fatalities: 117 },
    { year: 2012, accidents: 959, fatalities: 96 },
    { year: 2013, accidents: 870, fatalities: 127 },
    { year: 2014, accidents: 833, fatalities: 102 },
    { year: 2015, accidents: 926, fatalities: 105 },
    { year: 2016, accidents: 693, fatalities: 87 },
    { year: 2017, accidents: 773, fatalities: 96 },
    { year: 2018, accidents: 856, fatalities: 121 },
    { year: 2019, accidents: 843, fatalities: 116 },
    { year: 2020, accidents: 735, fatalities: 111 },
    { year: 2021, accidents: 746, fatalities: 100 },
    { year: 2022, accidents: 782, fatalities: 113 },
    { year: 2023, accidents: 728, fatalities: 120 },
    { year: 2024, accidents: 666, fatalities: 114 }
  ];

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // ---------------- Layout ----------------
  const VB_W = 1200;
  const VB_H = 360;

  const MARGINS = useMemo(
    () => (isMobile
      ? { left: 36, right: 18, top: 26, bottom: 54 }
      : { left: 70, right: 60, top: 36, bottom: 64 }
    ),
    [isMobile]
  );

  const X0 = MARGINS.left,  X1 = VB_W - MARGINS.right;
  const Y0 = MARGINS.top,   Y1 = VB_H - MARGINS.bottom;
  const CHART_W = X1 - X0;
  const CHART_H = Y1 - Y0;

  const maxAccidents  = useMemo(() => Math.max(...accidentData.map(d => d.accidents)), []);
  const maxFatalities = useMemo(() => Math.max(...accidentData.map(d => d.fatalities)), []);

  const stepX = useMemo(() => CHART_W / (accidentData.length - 1), [CHART_W, accidentData.length]);
  const xAt   = (i) => X0 + i * stepX;
  const yAcc  = (v) => Y1 - (v / maxAccidents)  * CHART_H;
  const yFat  = (v) => Y1 - (v / maxFatalities) * CHART_H;

  // ---------------- Transform (zoom/pan) ----------------
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  useEffect(() => { if (isMobile) setTx(0); }, [isMobile]);

  const MIN_SCALE = 1, MAX_SCALE = 4;
  const boundsFor = (s) => ({ minTx: X1 * (1 - s), maxTx: X0 * (1 - s) });
  const clampTx = (t, s) => {
    const { minTx, maxTx } = boundsFor(s);
    return Math.min(maxTx, Math.max(minTx, t));
  };

  // ---------------- Conversões (screen <-> viewBox) ----------------
  function svgMetrics() {
    const svg = svgRef.current;
    const rect = svg.getBoundingClientRect();
    const meet = Math.min(rect.width / VB_W, rect.height / VB_H);
    const xOff = (rect.width  - VB_W * meet) / 2;
    const yOff = (rect.height - VB_H * meet) / 2;
    return { rect, meet, xOff, yOff };
  }
  function clientToSvgXY(clientX, clientY) {
    const { rect, meet, xOff, yOff } = svgMetrics();
    return {
      xSvg: (clientX - rect.left - xOff) / meet,
      ySvg: (clientY - rect.top  - yOff) / meet,
      meet, xOff, yOff
    };
  }
  function xSvgToIndex(xSvgRaw) {
    const xSvg = (xSvgRaw - tx) / scale; // inverso do transform
    const raw = (xSvg - X0) / stepX;
    return Math.min(accidentData.length - 1, Math.max(0, Math.round(raw)));
  }
  function indexToTooltip(idx) {
    const { meet, xOff, yOff } = svgMetrics();
    const d = accidentData[idx];
    const cx = xAt(idx);
    const cy = Math.min(yAcc(d.accidents), yFat(d.fatalities)) - 10;
    const cxT = tx + scale * cx; // aplica transform
    return {
      index: idx, year: d.year, accidents: d.accidents, fatalities: d.fatalities,
      leftPx: xOff + cxT * meet,
      topPx:  yOff + cy  * meet,
      cx, cy
    };
  }

  // ---------------- Tooltip seguro ----------------
  const PADDING = 8;
  const handleLeave = () => { setTooltip(null); setTooltipPos(null); };

  function updateTooltipSafePosition(next) {
    if (!containerRef.current || !tooltipRef.current) return;
    const contRect = containerRef.current.getBoundingClientRect();
    const tipW = tooltipRef.current.offsetWidth;
    const tipH = tooltipRef.current.offsetHeight;

    // posição calculada já em px dentro do container
    let left = next.leftPx;
    let topAbove = next.topPx - tipH;
    let topBelow = next.topPx + 10;

    let placeAbove = !isMobile;
    let top = placeAbove ? topAbove : topBelow;

    if (placeAbove && top < PADDING) { placeAbove = false; top = topBelow; }
    if (!placeAbove && top + tipH > contRect.height - PADDING) { placeAbove = true; top = topAbove; }

    const minCenter = PADDING + tipW / 2;
    const maxCenter = contRect.width - PADDING - tipW / 2;
    left = Math.max(minCenter, Math.min(maxCenter, left));

    setTooltipPos({ left, top, placeAbove });
  }

  function setTooltipFromClient(clientX, clientY) {
    const { xSvg } = clientToSvgXY(clientX, clientY);
    const idx = xSvgToIndex(xSvg);
    const next = indexToTooltip(idx);
    setTooltip(next);
  }

  useEffect(() => {
    if (!tooltip) return;
    requestAnimationFrame(() => updateTooltipSafePosition(tooltip));
  }, [tooltip, isMobile, scale, tx]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onScroll = () => { if (tooltip) updateTooltipSafePosition(tooltip); };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [tooltip]);

  // ---------------- Zoom / Pan desktop ----------------
  function applyZoom(factor, xCenterSvg) {
    const sNew = Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale * factor));
    let tNew = tx + scale * xCenterSvg - sNew * xCenterSvg;
    if (!isMobile) tNew = clampTx(tNew, sNew); else tNew = 0;
    setScale(sNew); setTx(tNew);
  }
  function handleWheel(e) {
    if (!e.ctrlKey) return;
    e.preventDefault();
    const { xSvg } = clientToSvgXY(e.clientX, e.clientY);
    const f = e.deltaY < 0 ? 1.1 : 1/1.1;
    applyZoom(f, xSvg);
  }
  const dragRef = useRef({ active:false, x0:0, tx0:0 });
  function onMouseDown(e){
    if (isMobile) return;
    e.preventDefault();
    const { xSvg } = clientToSvgXY(e.clientX, e.clientY);
    dragRef.current = { active:true, x0:xSvg, tx0:tx };
  }
  function onMouseMove(e){
    if (isMobile) return;             // mobile não usa hover
    // sempre mostra tooltip no hover
    setTooltipFromClient(e.clientX, e.clientY);

    // se estiver arrastando, faz pan também
    if (dragRef.current.active){
      const { xSvg } = clientToSvgXY(e.clientX, e.clientY);
      const delta = xSvg - dragRef.current.x0;
      setTx(clampTx(dragRef.current.tx0 + delta, scale));
    }
  }
  function onMouseUp(){ dragRef.current.active = false; }

  // ---------------- Mobile: press para tooltip, swipe para scroll ----------------
  const gestureRef = useRef({ down:false, x:0, y:0, timer:0, moved:false });
  const TAP_MOVE_TOL = 8, PRESS_MS = 180;

  function onPointerDown(e){
    if (!isMobile) return;
    const x = e.clientX ?? e.touches?.[0]?.clientX;
    const y = e.clientY ?? e.touches?.[0]?.clientY;
    if (x == null || y == null) return;
    gestureRef.current = { down:true, x, y, moved:false, timer:0 };
    const t = window.setTimeout(() => {
      if (gestureRef.current.down && !gestureRef.current.moved) setTooltipFromClient(x, y);
    }, PRESS_MS);
    gestureRef.current.timer = t;
  }
  function onPointerMove(e){
    if (!isMobile || !gestureRef.current.down) return;
    const x = e.clientX ?? e.touches?.[0]?.clientX;
    const y = e.clientY ?? e.touches?.[0]?.clientY;
    if (x == null || y == null) return;
    const dx = Math.abs(x - gestureRef.current.x);
    const dy = Math.abs(y - gestureRef.current.y);
    if (dx > TAP_MOVE_TOL || dy > TAP_MOVE_TOL) {
      gestureRef.current.moved = true; window.clearTimeout(gestureRef.current.timer);
      setTooltip(null); setTooltipPos(null);
    }
  }
  function onPointerUp(e){
    if (!isMobile) return;
    const g = gestureRef.current;
    window.clearTimeout(g.timer);
    if (!g.moved) {
      const x = e.clientX ?? e.changedTouches?.[0]?.clientX;
      const y = e.clientY ?? e.changedTouches?.[0]?.clientY;
      if (x != null && y != null) setTooltipFromClient(x, y);
    }
    g.down = false;
  }

  // ---------------- Geometrias ----------------
  const accPoints = useMemo(() => accidentData.map((d, i) => `${xAt(i)},${yAcc(d.accidents)}`).join(' '), [xAt]);
  const fatPoints = useMemo(() => accidentData.map((d, i) => `${xAt(i)},${yFat(d.fatalities)}`).join(' '), [xAt]);

  // ---------------- Estilos ----------------
  const gridStroke  = 1.2;
  const lineStrokeA = isMobile ? 4.2 : 5.2;
  const lineStrokeF = isMobile ? 3.8 : 4.8;
  const rA = isMobile ? 4.5 : 5.5;
  const rF = isMobile ? 4.2 : 5.2;
  const fontAxis   = isMobile ? 12 : 14;
  const fontLegend = isMobile ? "text-base" : "text-lg";

  const mobileContentWidth = Math.max(900, VB_W * Math.max(1, scale * 1.05));

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="text-2xl md:text-3xl font-bold text-center">
          Acidentes Ferroviários e Vítimas Fatais
        </CardTitle>
        <p className="text-center text-gray-600">
          Evolução da segurança no transporte ferroviário brasileiro
        </p>
      </CardHeader>

      <CardContent>
        <div ref={scrollRef} className={isMobile ? "overflow-x-auto no-scrollbar" : ""}>
          <div
            ref={containerRef}
            className="relative"
            style={{ width: isMobile ? `${mobileContentWidth}px` : "100%", height: "420px" }}
          >
            <svg
              ref={svgRef}
              viewBox={`0 0 ${VB_W} ${VB_H}`}
              className="w-full h-full select-none"
              preserveAspectRatio="xMidYMid meet"
              // Desktop
              onWheel={handleWheel}
              onMouseDown={onMouseDown}
              onMouseUp={onMouseUp}
              onMouseMove={onMouseMove}     // <- HOVER no desktop
              onMouseLeave={handleLeave}
              // Mobile (não bloqueia pan-x)
              style={{ touchAction: isMobile ? "pan-x pinch-zoom" : "none" }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
            >
              {/* GRID */}
              {[0,1,2,3,4,5].map(i => (
                <line
                  key={i}
                  x1={X0}
                  y1={Y0 + i*(CHART_H/5)}
                  x2={X1}
                  y2={Y0 + i*(CHART_H/5)}
                  stroke="#e5e7eb"
                  strokeWidth={gridStroke}
                />
              ))}

              {/* Eixo Y label */}
              <text
                x={isMobile ? 10 : 18}
                y="180"
                transform={`rotate(-90, ${isMobile ? 10 : 18}, 180)`}
                textAnchor="middle"
                fontSize={12}
                fill="#6b7280"
              >
                Ocorrências / Vítimas
              </text>

              {/* Eixo X (anos) */}
              {accidentData.map((d, i) => (
                <text
                  key={d.year}
                  x={tx + scale * xAt(i)}
                  y={Y1 + 24}
                  textAnchor="middle"
                  fontSize={fontAxis}
                  fill="#6b7280"
                >
                  {d.year}
                </text>
              ))}

              {/* Séries */}
              <g transform={`translate(${tx},0) scale(${scale},1)`}>
                <polyline points={accPoints} fill="none" stroke="#ef4444" strokeWidth={lineStrokeA}/>
                {accidentData.map((d,i)=>(<circle key={`a-${i}`} cx={xAt(i)} cy={yAcc(d.accidents)} r={rA} fill="#ef4444"/>))}
                <polyline points={fatPoints} fill="none" stroke="#f97316" strokeWidth={lineStrokeF}/>
                {accidentData.map((d,i)=>(<circle key={`f-${i}`} cx={xAt(i)} cy={yFat(d.fatalities)} r={rF} fill="#f97316"/>))}
              </g>

              {/* Scrubber */}
              {tooltip && (
                <line
                  x1={tx + scale * xAt(tooltip.index)}
                  y1={Y0}
                  x2={tx + scale * xAt(tooltip.index)}
                  y2={Y1}
                  stroke="#9ca3af"
                  strokeWidth="1.2"
                  strokeDasharray="4"
                />
              )}

              <rect x="0" y="0" width={VB_W} height={VB_H} fill="transparent" className="cursor-crosshair"/>
            </svg>

            {/* Tooltip clamped */}
            {tooltip && (
              <div
                ref={tooltipRef}
                className="absolute z-10 bg-gray-900 text-white px-4 py-2 rounded-lg shadow-lg pointer-events-none"
                style={{
                  left: tooltipPos ? tooltipPos.left : -9999,
                  top:  tooltipPos ? tooltipPos.top  : -9999,
                  transform: tooltipPos?.placeAbove ? 'translate(-50%,-10%)' : 'translate(-50%,10%)',
                  whiteSpace: 'nowrap'
                }}
              >
                <div className="text-sm font-semibold">{tooltip.year}</div>
                <div className="text-xs text-red-300">Acidentes: {tooltip.accidents.toLocaleString('pt-BR')}</div>
                <div className="text-xs text-orange-300">Vítimas: {tooltip.fatalities}</div>
              </div>
            )}
          </div>
        </div>

        {/* Legenda */}
        <div className={`mt-6 flex justify-center gap-8 ${fontLegend}`}>
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 bg-red-500 rounded"></div>
            <span className="font-medium text-gray-800">Acidentes</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 bg-orange-500 rounded"></div>
            <span className="font-medium text-gray-800">Vítimas Fatais</span>
          </div>
        </div>

             <div className="mt-6 grid md:grid-cols-2 gap-6">
          <div className="bg-green-50 p-4 rounded-lg">
            <h4 className="font-semibold text-green-900 mb-2">Melhoria em 2024</h4>
            <p className="text-green-800 text-sm">
              <strong>666 acidentes</strong> — redução de 8,5% em relação a 2023.
              Menor número registrado desde 2010.
            </p>
          </div>
          <div className="bg-orange-50 p-4 rounded-lg">
            <h4 className="font-semibold text-orange-900 mb-2">Principais Causas</h4>
            <p className="text-orange-800 text-sm">
              Passagens em nível, falhas humanas, precariedade da via permanente
              e condições climáticas adversas.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
